import React from "react";
import { Box, Text, Stack } from "@bookingcom/bui-react";
import { trackWithDefaultStage } from "@flights/et-universal";
import { useI18n } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { SearchboxController, SearchBoxHorizontalDefault } from "@bookingcom/flights-searchbox";
import Container from "../Container/index.js";
import NoJS from "../../../screens/NoJS.js";
import useSearchbox from "../../../hooks/useSearchbox.js";
import useLocaleContext from "../../../hooks/useLocaleContext.js";
import useLandingPageEvents from "../../../hooks/c360/useLandingPageEvents.js";
import { SPRING_DEAL_BG_COLOR, SPRING_DEAL_IMAGE_URL_INDEX, SUMMER_DEAL_2026_BG_COLOR, SUMMER_DEAL_2026_IMAGE_URL_INDEX } from "../../../constants.js";
import styles from "./HomeSearchHeader.desktop.module.css";
import { mcn } from "../../../utils/mergeClassnames.js";
import { useSpringDeal } from "../../../hooks/useSpringDeal.js";
import { useSummerDeal } from "../../../hooks/useSummerDeal.js";
import useGlobalContext from "../../../hooks/useGlobalContext.js";
import useFareCalendarEligible from "../../../hooks/useFareCalendarEligible.js";
import { isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
const HomeSearchHeader = ({ title, subtitle, renderSearchbox } = {}) => {
    const i18n = useI18n();
    const searchBoxProps = useSearchbox();
    const { isRTL } = useLocaleContext();
    const trackLandingPageEvents = useLandingPageEvents();
    const { isSpringDealExp } = useSpringDeal();
    const { isSummerDealExp } = useSummerDeal();
    const { currencyCode } = useGlobalContext();
    const isFareCalendarEligible = useFareCalendarEligible();
    const hasDeal = isSpringDealExp || isSummerDealExp;
    const titleStyles = mcn(styles.title, hasDeal && styles.titleWithDeal);
    const subtitleStyles = mcn(styles.subtitle, hasDeal && styles.subtitleWithDeal);
    const titleBoxStyles = mcn(styles.titleBox, hasDeal && styles.titleBoxSpringDeal);
    const titleStackStyles = mcn(styles.titleStack, hasDeal && styles.titleStackSpringDeal);
    const titleStackItemStyles = mcn(styles.titleStackItem, hasDeal && styles.titleStackItemSpringDeal);
    const springDealBgStyle = isSpringDealExp
        ? {
            background: `linear-gradient(90deg, rgba(0, 0, 0, 0.8) 45.19%, rgba(0, 0, 0, 0) 100%), url(${SPRING_DEAL_IMAGE_URL_INDEX}) ${SPRING_DEAL_BG_COLOR} 0px -100px / cover no-repeat`
        }
        : undefined;
    const summerDealBgStyle = isSummerDealExp
        ? {
            background: `linear-gradient(90deg, rgba(0, 0, 0, 0.8) 45.19%, rgba(0, 0, 0, 0) 100%), url(${SUMMER_DEAL_2026_IMAGE_URL_INDEX}) ${SUMMER_DEAL_2026_BG_COLOR} center / cover no-repeat`
        }
        : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { className: titleBoxStyles },
            React.createElement(Container, { className: styles.titleContainer, style: summerDealBgStyle ?? springDealBgStyle },
                isSummerDealExp && React.createElement("span", { role: "img", "aria-label": i18n.trans(t("a11y_flights_cat_summer_26_alt_image")) }),
                !isSummerDealExp && isSpringDealExp && (React.createElement("span", { role: "img", "aria-label": i18n.trans(t("a11y_flights_spring26_hero_image_alt")) })),
                React.createElement(NoJS, null),
                React.createElement(Stack, { gap: 1, className: titleStackStyles, direction: "row", alignItems: "center", justifyContent: "space-between" },
                    React.createElement(Stack.Item, { grow: true, className: titleStackItemStyles },
                        React.createElement(Text, { variant: "display_3", "data-testid": "home_header", tagName: "h1", color: "white", className: titleStyles }, title ??
                            (isSummerDealExp
                                ? i18n.trans(t("flights_cat_summer26_index_header"))
                                : isSpringDealExp
                                    ? i18n.trans(t("flights_spring26_index_header"))
                                    : i18n.trans(t("flights_index_header_compare_book_ease")))),
                        React.createElement(Text, { variant: "featured_2", color: "white", className: subtitleStyles }, subtitle ??
                            (isSummerDealExp
                                ? i18n.trans(t("flights_cat_summer26_index_subheader"))
                                : isSpringDealExp
                                    ? i18n.trans(t("flights_spring26_index_subheader"))
                                    : i18n.trans(t("flights_index_header_discover_next_dream")))))))),
        renderSearchbox !== null && (React.createElement(Box, { className: `${styles.searchboxControllerBox} ${styles.searchboxControllerBoxRes}` },
            React.createElement(Container, { className: `${styles.searchBoxControllerContainerRes}` }, renderSearchbox ?? (React.createElement(SearchboxController, { i18n: i18n, ...searchBoxProps, hideShadow: true, isRTL: isRTL, isHeaderUpdateExp: true, isResponsiveFixes: true, enabledCountriesAndAnywhere: true, allowEmptyDestination: true, isSpringDealExp: isSpringDealExp, isSummerDealExp: isSummerDealExp, isSearchAnyCityEnabled: true, fareCalendarConfig: {
                    isFareCalendarDesktopEnabled: isFareCalendarEligible &&
                        (!!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1) ||
                            !!trackWithDefaultStage("flights_web_cat_fare_calendar_data_collection", 1)),
                    isFareCalendarDisplayEnabled: isFareCalendarEligible && !!trackWithDefaultStage("flights_web_cat_fare_calendar_oneway", 1),
                    currencyCode
                }, isCalendarReflowFixEnabled: isFeatureRunningClientSide("FLIGHTS_WEB_CAT_CALENDAR_FIX"), onSearch: (searchParams) => {
                    trackLandingPageEvents("click", "searchbox");
                    searchBoxProps.onSearch(searchParams);
                }, showCabinClassLabel: !!trackWithDefaultStage("flights_cat_cabin_class_label", 1) }, SearchBoxHorizontalDefault)))))));
};
export default HomeSearchHeader;
